import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCurrency } from '../../hooks/useCurrency';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';

export default function HomePage() {
  const { user, logout } = useAuth();
  const { formatAmount } = useCurrency();

  return (
    <div className="min-h-screen bg-gray-50">
      <Header user={user} onLogout={logout} />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/b92d623ef2e9e0f8f771bf85065ea7a9.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Welcome to <span className="text-blue-400">BRIGHT STONE CREDITS</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              <strong>Fast • Affordable • Reliable</strong><br />
              Quick and flexible Business, Personal, School Fees, and Emergency Loans with investment opportunities to help you achieve financial freedom.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Link to="/register">
                <Button size="lg" className="text-lg px-8 py-4">
                  <i className="ri-user-add-line mr-2"></i>
                  Get Started Today
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button variant="outline" size="lg" className="text-lg px-8 py-4 bg-white/10 border-white text-white hover:bg-white hover:text-gray-900">
                  <i className="ri-money-dollar-circle-line mr-2"></i>
                  Apply for Loan
                </Button>
              </Link>
            </div>
            <div className="flex items-center space-x-6 text-white">
              <div className="flex items-center">
                <i className="ri-shield-check-line text-green-400 mr-2"></i>
                <span>100% Legitimate</span>
              </div>
              <div className="flex items-center">
                <i className="ri-time-line text-blue-400 mr-2"></i>
                <span>10 Min Processing</span>
              </div>
              <div className="flex items-center">
                <i className="ri-percent-line text-yellow-400 mr-2"></i>
                <span>2.5% Interest</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose BRIGHT STONE CREDITS?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We are a <strong>Registered Firm</strong>, incorporated under the <strong>Companies Act, 2015</strong>, 
              making us a 100% legitimate and spam-free financial institution.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <Card className="text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-blue-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Fast Processing</h3>
              <p className="text-gray-600">Get your loan approved and disbursed within 10 minutes after share verification.</p>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-percent-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Low Interest Rate</h3>
              <p className="text-gray-600">Enjoy affordable loans with just 2.5% interest rate on the principal amount.</p>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-purple-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">100% Legitimate</h3>
              <p className="text-gray-600">Registered under Companies Act, 2015. Fully licensed and regulated financial institution.</p>
            </Card>
          </div>

          {/* Certificate Section */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-8 shadow-lg">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-blue-900 mb-4">
                <i className="ri-award-line text-blue-600 mr-3"></i>
                Certificate of Incorporation
              </h3>
              <p className="text-blue-700 text-lg max-w-2xl mx-auto">
                Official registration certificate proving our legitimacy as a private limited company under the Companies Act 2006.
              </p>
            </div>
            
            <div className="flex justify-center mb-6">
              <div className="bg-white rounded-xl shadow-lg p-4 max-w-md">
                <img 
                  src="https://static.readdy.ai/image/aa741f9682da4f6fbea906ee950084e2/9a7354fb73b4c2c93111812cabcacf32.jpeg"
                  alt="BRIGHT STONE CREDITS Certificate of Incorporation - Company Number 14682349"
                  className="w-full h-auto rounded-lg shadow-md"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-building-line text-white text-xl"></i>
                </div>
                <h4 className="font-semibold text-blue-900 mb-2">Company Number</h4>
                <p className="text-blue-700 font-mono text-lg">14682349</p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-calendar-line text-white text-xl"></i>
                </div>
                <h4 className="font-semibold text-blue-900 mb-2">Incorporated</h4>
                <p className="text-blue-700 font-semibold">9th February 2021</p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-shield-check-line text-white text-xl"></i>
                </div>
                <h4 className="font-semibold text-blue-900 mb-2">Status</h4>
                <p className="text-green-600 font-semibold">Active & Verified</p>
              </div>
            </div>

            <div className="text-center mt-6">
              <div className="inline-flex items-center space-x-2 bg-green-100 text-green-800 px-4 py-2 rounded-lg">
                <i className="ri-verified-badge-line text-xl"></i>
                <span className="font-semibold">Officially Registered Private Limited Company</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600">Simple steps to get your loan approved</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Create Account</h3>
              <p className="text-gray-600">Sign up with your email address. One account per email only.</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Purchase Shares</h3>
              <p className="text-gray-600">Buy shares between KSh 2,500 - KSh 500,000 to qualify for loans up to 20x your share value.</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Get Qualified</h3>
              <p className="text-gray-600">Qualify for loans up to 20 times your share value with flexible repayment terms.</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">4</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Receive Funds</h3>
              <p className="text-gray-600">Get your loan disbursed within 10 minutes of approval.</p>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Support Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-12 border-2 border-green-100">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-8 md:space-y-0 md:space-x-12">
              <div className="flex items-center space-x-6">
                <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                  <i className="ri-whatsapp-line text-white text-4xl"></i>
                </div>
                <div className="text-left">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Need Help?</h2>
                  <p className="text-gray-600 text-lg">Chat with us on WhatsApp - Available 24/7</p>
                  <p className="text-green-600 font-semibold mt-2">+254740831354</p>
                </div>
              </div>
              <a
                href="https://wa.me/254740831354"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center py-4 px-8 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all duration-300 transform hover:scale-105 shadow-lg font-semibold text-lg whitespace-nowrap"
              >
                <i className="ri-whatsapp-line mr-3 text-2xl"></i>
                Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied customers in Kenya! 
            Empower your dreams, grow your business, and improve your life standard.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-4">
                <i className="ri-user-add-line mr-2"></i>
                Create Account
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-600 text-lg px-8 py-4">
                <i className="ri-customer-service-line mr-2"></i>
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
